import { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { X, Calendar, Send, AlertCircle, Sparkles, Clock, Loader2, ImagePlus, Trash2, Library } from 'lucide-react';
import { postsApi, socialAccountsApi, queuesApi, aiApi, mediaApi, type YouTubePostOptions } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { PLATFORMS, type Platform } from '@/lib/platforms';
import { cn } from '@/lib/utils';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { xWeightedLength } from '@/lib/x-length';
import { MediaLibraryPicker, type PickedMedia } from './MediaLibraryPicker';
import { CaptionToolbar } from './CaptionToolbar';

interface ComposerProps {
  onClose: () => void;
  editPostId?: string;
  /** Pre-attach these R2 keys (e.g. "Use in post" from Media Library). */
  initialMediaKeys?: string[];
  /** Prefill the main caption (e.g. "Use this idea" from Trends). */
  initialContent?: string;
}

interface Attachment {
  key: string;
  isVideo: boolean;
}

// Infer video vs image from the R2 key extension (used when we only have the
// key, e.g. a deep-link from Media Library).
const isVideoKey = (key: string) => /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(key);

// ISO UTC → datetime-local (naive local time) for an <input type="datetime-local">.
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// The start of the next hour, as a datetime-local value.
const nextHourLocalInput = () => {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return toLocalInput(d.toISOString());
};

// US format for the schedule field, whatever the browser's language: 11/02/2026.
const US_DATE: Intl.DateTimeFormatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };

// Shape of a stored per_platform_overrides entry (keyed by account id).
interface StoredOverride {
  content?: string;
  youtube?: YouTubePostOptions;
  tiktok?: {
    privacy_level?: string;
    disable_comment?: boolean;
    disable_duet?: boolean;
    disable_stitch?: boolean;
  };
  gmb?: {
    topic_type?: 'STANDARD' | 'EVENT' | 'OFFER';
    cta_type?: string;
    cta_url?: string;
    event_title?: string;
    event_start?: string;
    event_end?: string;
  };
}

export function Composer({ onClose, editPostId, initialMediaKeys, initialContent }: ComposerProps) {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);

  const [content, setContent] = useState(initialContent ?? '');
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('main');
  const [scheduleAt, setScheduleAt] = useState('');
  const [perPlatformContent, setPerPlatformContent] = useState<Record<string, string>>({});
  const [queueId, setQueueId] = useState<string>('');
  const [media, setMedia] = useState<Attachment[]>(() =>
    (initialMediaKeys ?? []).map((k) => ({ key: k, isVideo: isVideoKey(k) }))
  );
  const [isUploading, setIsUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // YouTube-specific options (shown when a YouTube account is selected)
  const thumbInputRef = useRef<HTMLInputElement>(null);
  const [ytPrivacy, setYtPrivacy] = useState<'public' | 'unlisted' | 'private'>('public');
  const [ytCategoryId, setYtCategoryId] = useState('');
  const [ytTags, setYtTags] = useState('');
  const [ytMadeForKids, setYtMadeForKids] = useState(false);
  const [ytThumbnailKey, setYtThumbnailKey] = useState('');
  const [ytThumbUploading, setYtThumbUploading] = useState(false);

  // TikTok options (shown when a TikTok account is selected)
  const [ttPrivacy, setTtPrivacy] = useState('');
  const [ttDisableComment, setTtDisableComment] = useState(false);
  const [ttDisableDuet, setTtDisableDuet] = useState(false);
  const [ttDisableStitch, setTtDisableStitch] = useState(false);

  // Google Business Profile options (shown when a GMB account is selected)
  const [gmbTopicType, setGmbTopicType] = useState<'STANDARD' | 'EVENT' | 'OFFER'>('STANDARD');
  const [gmbCtaType, setGmbCtaType] = useState('');
  const [gmbCtaUrl, setGmbCtaUrl] = useState('');
  const [gmbEventTitle, setGmbEventTitle] = useState('');
  const [gmbEventStart, setGmbEventStart] = useState('');
  const [gmbEventEnd, setGmbEventEnd] = useState('');

  // Edit mode: load the post and hydrate the form once.
  const hydratedRef = useRef(false);
  // Whether the loaded post had a schedule — clearing the date then means
  // "unschedule" (back to draft), not "leave as is".
  const [hadSchedule, setHadSchedule] = useState(false);
  const { data: editData } = useQuery({
    queryKey: ['post', editPostId],
    queryFn: async () => {
      const token = await getToken();
      return postsApi.get(token!, editPostId!);
    },
    enabled: !!editPostId,
  });

  useEffect(() => {
    const post = editData?.post;
    if (!post || hydratedRef.current) return;
    hydratedRef.current = true;

    setContent(post.content);
    try {
      setSelectedAccounts(JSON.parse(post.platforms) as string[]);
    } catch {
      /* keep empty selection on malformed data */
    }
    if (post.media_keys) {
      try {
        const keys = JSON.parse(post.media_keys) as string[];
        setMedia(keys.map((k) => ({ key: k, isVideo: isVideoKey(k) })));
      } catch {
        /* ignore malformed media_keys */
      }
    }
    if (post.scheduled_at) {
      setScheduleAt(toLocalInput(post.scheduled_at));
      setHadSchedule(post.status === 'scheduled');
    }
    if (post.per_platform_overrides) {
      try {
        const overrides = JSON.parse(post.per_platform_overrides) as Record<string, StoredOverride>;
        const perContent: Record<string, string> = {};
        for (const [id, o] of Object.entries(overrides)) {
          if (o?.content) perContent[id] = o.content;
          // Restore the per-platform settings too: buildOverrides rebuilds
          // them from this state on save, so leaving the defaults would turn
          // a private video public, drop tags/thumbnail, etc.
          if (o?.youtube) {
            setYtPrivacy(o.youtube.privacy ?? 'public');
            setYtCategoryId(o.youtube.categoryId ?? '');
            setYtTags((o.youtube.tags ?? []).join(', '));
            setYtMadeForKids(!!o.youtube.madeForKids);
            setYtThumbnailKey(o.youtube.thumbnailKey ?? '');
          }
          if (o?.tiktok) {
            setTtPrivacy(o.tiktok.privacy_level ?? '');
            setTtDisableComment(!!o.tiktok.disable_comment);
            setTtDisableDuet(!!o.tiktok.disable_duet);
            setTtDisableStitch(!!o.tiktok.disable_stitch);
          }
          if (o?.gmb) {
            setGmbTopicType(o.gmb.topic_type ?? 'STANDARD');
            setGmbCtaType(o.gmb.cta_type ?? '');
            setGmbCtaUrl(o.gmb.cta_url ?? '');
            setGmbEventTitle(o.gmb.event_title ?? '');
            setGmbEventStart(o.gmb.event_start ? toLocalInput(o.gmb.event_start) : '');
            setGmbEventEnd(o.gmb.event_end ? toLocalInput(o.gmb.event_end) : '');
          }
        }
        setPerPlatformContent(perContent);
      } catch {
        /* ignore malformed overrides */
      }
    }
  }, [editData]);

  const { data: accountsData } = useQuery({
    queryKey: ['social-accounts', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return socialAccountsApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  const { data: queuesData } = useQuery({
    queryKey: ['queues', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return queuesApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  const accounts = accountsData?.accounts ?? [];
  const queues = queuesData?.queues ?? [];
  // Only accounts still connected to this workspace. An edited post can carry
  // IDs of since-disconnected accounts; they render as nothing (so can't be
  // deselected) and the API rejects them, so they're never sent. Empty until
  // the account list has loaded, which keeps Save disabled until then.
  const connectedSelected = accountsData
    ? selectedAccounts.filter((id) => accounts.some((a) => a.id === id))
    : [];

  const youtubeAccount = accounts.find(
    (a) => a.platform === 'youtube' && selectedAccounts.includes(a.id)
  );
  const tiktokAccount = accounts.find(
    (a) => a.platform === 'tiktok' && selectedAccounts.includes(a.id)
  );
  const gmbAccount = accounts.find(
    (a) => a.platform === 'gmb' && selectedAccounts.includes(a.id)
  );

  // Build per-platform overrides (per-account content + platform settings).
  const buildOverrides = () => {
    const o: Record<
      string,
      {
        content?: string;
        youtube?: YouTubePostOptions;
        tiktok?: Record<string, unknown>;
        gmb?: Record<string, unknown>;
      }
    > = {};
    for (const [accountId, text] of Object.entries(perPlatformContent)) {
      if (text && text !== content) o[accountId] = { content: text };
    }
    if (youtubeAccount) {
      o[youtubeAccount.id] = {
        ...(o[youtubeAccount.id] ?? {}),
        youtube: {
          privacy: ytPrivacy,
          categoryId: ytCategoryId || undefined,
          tags: ytTags ? ytTags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
          madeForKids: ytMadeForKids,
          thumbnailKey: ytThumbnailKey || undefined,
        },
      };
    }
    if (tiktokAccount) {
      o[tiktokAccount.id] = {
        ...(o[tiktokAccount.id] ?? {}),
        tiktok: {
          privacy_level: ttPrivacy || undefined,
          disable_comment: ttDisableComment,
          disable_duet: ttDisableDuet,
          disable_stitch: ttDisableStitch,
        },
      };
    }
    if (gmbAccount && (gmbCtaType || gmbTopicType !== 'STANDARD')) {
      o[gmbAccount.id] = {
        ...(o[gmbAccount.id] ?? {}),
        gmb: {
          topic_type: gmbTopicType,
          cta_type: gmbCtaType || undefined,
          cta_url: gmbCtaUrl || undefined,
          event_title: gmbEventTitle || undefined,
          event_start: gmbEventStart ? new Date(gmbEventStart).toISOString() : undefined,
          event_end: gmbEventEnd ? new Date(gmbEventEnd).toISOString() : undefined,
        },
      };
    }
    return o;
  };

  // Edit mode: the post was scheduled and the user cleared its date.
  const isUnscheduling = !!editPostId && hadSchedule && !scheduleAt && !queueId;

  // Starts from a complete date and time: an empty input only reports a value
  // once both are picked, so choosing just a day would look like nothing happened.
  const openSchedulePicker = (input: HTMLInputElement) => {
    if (!input.value) flushSync(() => setScheduleAt(nextHourLocalInput()));
    try {
      input.showPicker();
    } catch {
      // Not allowed or supported here; the field keeps focus, so it can be typed.
    }
  };

  const generateCaption = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return aiApi.generateCaption(token!, {
        workspaceId: currentWorkspace!.id,
        mediaKey: media[0]?.key ?? '',
        platform: accounts.find((a) => a.id === selectedAccounts[0])?.platform,
      });
    },
    onSuccess: (resp) => {
      if (activeTab === 'main') setContent(resp.caption);
      else setPerPlatformContent((prev) => ({ ...prev, [activeTab]: resp.caption }));
    },
  });

  const createPost = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const overrides = buildOverrides();
      return postsApi.create(token!, {
        workspace_id: currentWorkspace!.id,
        content,
        platforms: connectedSelected,
        media_keys: media.length ? media.map((m) => m.key) : undefined,
        // datetime-local gives a naive LOCAL time; convert to UTC ISO so the
        // backend cron (which compares against UTC now) fires at the right time.
        scheduled_at: queueId || !scheduleAt ? undefined : new Date(scheduleAt).toISOString(),
        status: queueId ? undefined : scheduleAt ? 'scheduled' : 'draft',
        per_platform_overrides: Object.keys(overrides).length ? overrides : undefined,
        queue_id: queueId || undefined,
      });
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] });
      qc.invalidateQueries({ queryKey: ['queues', currentWorkspace?.id] });
      // Post created but queue placement failed — keep the modal open and say so.
      if (resp.enqueued === false) {
        setErrorMsg(
          `Saved as draft, but adding to the queue failed: ${resp.enqueue_error ?? 'unknown error'}`
        );
        return;
      }
      onClose();
    },
    onError: (err) =>
      setErrorMsg(err instanceof Error ? err.message : 'Failed to save the post'),
  });

  // Edit mode save: PATCH the existing post, then optionally slot it into a queue.
  const updatePost = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const overrides = buildOverrides();
      const updated = await postsApi.update(token!, editPostId!, {
        content,
        platforms: connectedSelected,
        media_keys: media.map((m) => m.key),
        // Date set → (re)schedule; date cleared on a scheduled post →
        // unschedule back to draft; otherwise leave the schedule alone.
        scheduled_at:
          scheduleAt && !queueId ? new Date(scheduleAt).toISOString() : isUnscheduling ? null : undefined,
        status: scheduleAt && !queueId ? 'scheduled' : isUnscheduling ? 'draft' : undefined,
        // Always send the full set so removed per-account text/settings clear.
        per_platform_overrides: overrides,
      });
      if (queueId) {
        await queuesApi.enqueue(token!, queueId, editPostId!);
      }
      return updated;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] });
      qc.invalidateQueries({ queryKey: ['post', editPostId] });
      qc.invalidateQueries({ queryKey: ['queues', currentWorkspace?.id] });
      onClose();
    },
    onError: (err) =>
      setErrorMsg(err instanceof Error ? err.message : 'Failed to save changes'),
  });

  const publishNow = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const overrides = buildOverrides();
      return postsApi.create(token!, {
        workspace_id: currentWorkspace!.id,
        content,
        platforms: connectedSelected,
        media_keys: media.length ? media.map((m) => m.key) : undefined,
        status: 'queued',
        per_platform_overrides: Object.keys(overrides).length ? overrides : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] });
      onClose();
    },
    onError: (err) =>
      setErrorMsg(err instanceof Error ? err.message : 'Failed to publish the post'),
  });

  const handleFilesSelect = async (files: File[]) => {
    if (!currentWorkspace || files.length === 0) return;
    setIsUploading(true);
    try {
      const token = await getToken();
      for (const file of files) {
        const result = await mediaApi.upload(token!, currentWorkspace.id, file);
        setMedia((prev) => [
          ...prev,
          { key: result.media.r2_key, isVideo: file.type.startsWith('video') },
        ]);
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? `Upload failed: ${err.message}` : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const addFromLibrary = (picked: PickedMedia[]) => {
    setMedia((prev) => {
      const existing = new Set(prev.map((m) => m.key));
      const fresh = picked.filter((p) => !existing.has(p.key));
      return [...prev, ...fresh];
    });
  };

  const removeMedia = (key: string) => {
    setMedia((prev) => prev.filter((m) => m.key !== key));
  };

  const handleThumbSelect = async (file: File) => {
    if (!currentWorkspace) return;
    setYtThumbUploading(true);
    try {
      const token = await getToken();
      const result = await mediaApi.upload(token!, currentWorkspace.id, file);
      setYtThumbnailKey(result.media.r2_key);
    } catch (err) {
      setErrorMsg(err instanceof Error ? `Thumbnail upload failed: ${err.message}` : 'Thumbnail upload failed');
    } finally {
      setYtThumbUploading(false);
    }
  };

  const toggleAccount = (id: string) => {
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const getActiveContent = () => {
    if (activeTab === 'main') return content;
    return perPlatformContent[activeTab] ?? content;
  };

  const setActiveContent = (text: string) => {
    if (activeTab === 'main') {
      setContent(text);
    } else {
      setPerPlatformContent((prev) => ({ ...prev, [activeTab]: text }));
    }
  };

  const activeAccount = accounts.find((a) => a.id === activeTab);
  const platformData = activeAccount
    ? PLATFORMS[activeAccount.platform as Platform]
    : null;
  const charLimit = platformData?.charLimit ?? 5000;
  const currentText = getActiveContent();
  // X threads: `---` on its own line splits into tweets, so the limit applies
  // to the longest segment, not the whole draft.
  const isXTab = activeAccount?.platform === 'x';
  // X counts links as 23 chars and emoji/CJK as 2 — use its weighted length.
  const measure = (s: string) => (isXTab ? xWeightedLength(s) : s.length);
  const segments = isXTab
    ? currentText.split(/\n\s*---\s*\n/).map((s) => s.trim()).filter(Boolean)
    : [currentText];
  const longestSegment = segments.reduce((max, s) => Math.max(max, measure(s)), 0);
  const currentLength = measure(currentText);
  const isOverLimit = (isXTab ? longestSegment : currentLength) > charLimit;
  const isThread = isXTab && segments.length > 1;
  // A post is valid with text in ANY tab (main or per-platform) OR attached media.
  const hasContent =
    content.trim().length > 0 ||
    Object.values(perPlatformContent).some((v) => (v ?? '').trim().length > 0) ||
    media.length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">
            {editPostId ? 'Edit Post' : 'Create Post'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {errorMsg && (
          <div className="flex items-start gap-2 px-4 py-2.5 border-b border-border bg-destructive/10 text-destructive text-xs">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="flex-1">{errorMsg}</span>
            <button
              onClick={() => setErrorMsg(null)}
              className="shrink-0 hover:opacity-70"
              aria-label="Dismiss error"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Account selector */}
          <div className="w-48 border-r border-border p-3 overflow-y-auto shrink-0">
            <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
              Accounts
            </p>
            {accounts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No connected accounts</p>
            ) : (
              <div className="space-y-1">
                {accounts.map((account) => {
                  return (
                    <button
                      key={account.id}
                      onClick={() => toggleAccount(account.id)}
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs transition-colors',
                        selectedAccounts.includes(account.id)
                          ? 'bg-accent/15 text-foreground ring-1 ring-accent/40 font-medium'
                          : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                      )}
                    >
                      <PlatformIcon platform={account.platform} size="sm" labelled />
                      <span className="truncate">{account.account_name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Center: Editor */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Platform tabs */}
            {selectedAccounts.length > 0 && (
              <div className="flex items-center gap-0.5 px-3 pt-3 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('main')}
                  className={cn(
                    'px-3 py-1.5 text-xs rounded-t-md border-b-2 transition-colors shrink-0',
                    activeTab === 'main'
                      ? 'border-accent text-accent bg-accent/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  Main
                </button>
                {selectedAccounts.map((id) => {
                  const account = accounts.find((a) => a.id === id);
                  if (!account) return null;
                  const platform = PLATFORMS[account.platform as Platform];
                  return (
                    <button
                      key={id}
                      onClick={() => setActiveTab(id)}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-md border-b-2 transition-colors shrink-0',
                        activeTab === id
                          ? 'text-foreground bg-accent/5'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      )}
                      style={activeTab === id ? { borderBottomColor: platform?.color } : {}}
                    >
                      <PlatformIcon platform={account.platform} size="xs" variant="glyph" />
                      {platform?.label ?? account.platform}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
              <CaptionToolbar
                value={currentText}
                onChange={setActiveContent}
                textareaRef={captionRef}
              />

              <textarea
                ref={captionRef}
                value={currentText}
                onChange={(e) => setActiveContent(e.target.value)}
                placeholder="What's on your mind? Write your post..."
                className={cn(
                  'bg-input border rounded-lg p-3 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none focus:ring-1 focus:ring-accent min-h-32',
                  isOverLimit ? 'border-destructive' : 'border-border'
                )}
              />

              {/* Char count */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isOverLimit && (
                    <div className="flex items-center gap-1 text-destructive text-xs">
                      <AlertCircle className="h-3.5 w-3.5" />
                      <span>{isThread ? 'A tweet in the thread is over the limit' : 'Over character limit'}</span>
                    </div>
                  )}
                  {isXTab && !isThread && (
                    <span className="text-[11px] text-muted-foreground">
                      Tip: a line with --- splits this into a thread
                    </span>
                  )}
                  {isThread && !isOverLimit && (
                    <span className="text-[11px] text-accent">
                      Thread · {segments.length} tweets
                    </span>
                  )}
                </div>
                <span className={cn('text-xs', isOverLimit ? 'text-destructive' : 'text-muted-foreground')}>
                  {isThread ? `${longestSegment} / ${charLimit} (longest tweet)` : `${currentLength} / ${charLimit}`}
                </span>
              </div>

              {/* Media attachment */}
              <div className="space-y-2">
                {media.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {media.map((m) => (
                      <div key={m.key} className="relative">
                        {m.isVideo ? (
                          <video
                            src={mediaApi.serveUrl(m.key)}
                            controls
                            className="h-24 w-24 rounded-lg border border-border object-cover bg-black"
                          />
                        ) : (
                          <img
                            src={mediaApi.serveUrl(m.key)}
                            alt="Attached media"
                            className="h-24 w-24 rounded-lg border border-border object-cover"
                          />
                        )}
                        <button
                          onClick={() => removeMedia(m.key)}
                          className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {/* Upload from device */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md bg-muted/40 text-muted-foreground border border-border hover:bg-muted/60 hover:text-foreground disabled:opacity-50 transition-colors"
                  >
                    {isUploading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <ImagePlus className="h-3 w-3" />
                    )}
                    {isUploading ? 'Uploading…' : 'Upload media'}
                  </button>

                  {/* Pick from Media Library */}
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md bg-muted/40 text-muted-foreground border border-border hover:bg-muted/60 hover:text-foreground transition-colors"
                  >
                    <Library className="h-3 w-3" />
                    From Library
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime"
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      if (files.length) handleFilesSelect(files);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => generateCaption.mutate()}
                    disabled={media.length === 0 || generateCaption.isPending}
                    title={media.length ? 'Generate a caption from the attached media' : 'Attach media first'}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md bg-accent/15 text-accent ring-1 ring-accent/30 hover:bg-accent/25 disabled:opacity-50 transition-colors"
                  >
                    {generateCaption.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    Generate Caption
                  </button>
                </div>
              </div>

              {/* YouTube options */}
              {youtubeAccount && (
                <div className="border border-border rounded-lg p-3 space-y-2.5">
                  <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <PlatformIcon platform="youtube" size="xs" variant="glyph" />
                    YouTube options
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-[11px] text-muted-foreground">
                      Privacy
                      <select
                        value={ytPrivacy}
                        onChange={(e) => setYtPrivacy(e.target.value as 'public' | 'unlisted' | 'private')}
                        className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
                      >
                        <option value="public">Public</option>
                        <option value="unlisted">Unlisted</option>
                        <option value="private">Private</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-[11px] text-foreground self-end pb-1.5">
                      <input
                        type="checkbox"
                        checked={ytMadeForKids}
                        onChange={(e) => setYtMadeForKids(e.target.checked)}
                      />
                      Made for kids
                    </label>
                  </div>
                  <label className="block text-[11px] text-muted-foreground">
                    Category
                    <select
                      value={ytCategoryId}
                      onChange={(e) => setYtCategoryId(e.target.value)}
                      className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
                    >
                      <option value="">Default</option>
                      <option value="1">Film & Animation</option>
                      <option value="2">Autos & Vehicles</option>
                      <option value="10">Music</option>
                      <option value="15">Pets & Animals</option>
                      <option value="17">Sports</option>
                      <option value="19">Travel & Events</option>
                      <option value="20">Gaming</option>
                      <option value="22">People & Blogs</option>
                      <option value="23">Comedy</option>
                      <option value="24">Entertainment</option>
                      <option value="25">News & Politics</option>
                      <option value="26">Howto & Style</option>
                      <option value="27">Education</option>
                      <option value="28">Science & Technology</option>
                    </select>
                  </label>
                  <label className="block text-[11px] text-muted-foreground">
                    Tags (comma-separated)
                    <input
                      value={ytTags}
                      onChange={(e) => setYtTags(e.target.value)}
                      placeholder="news, update, sfx"
                      className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
                    />
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => thumbInputRef.current?.click()}
                      disabled={ytThumbUploading}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md bg-muted/40 text-muted-foreground border border-border hover:bg-muted/60 hover:text-foreground disabled:opacity-50 transition-colors"
                    >
                      {ytThumbUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />}
                      {ytThumbnailKey ? 'Thumbnail set ✓' : 'Custom thumbnail'}
                    </button>
                    {ytThumbnailKey && (
                      <button
                        type="button"
                        onClick={() => setYtThumbnailKey('')}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <input
                      ref={thumbInputRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleThumbSelect(file);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* TikTok options */}
              {tiktokAccount && (
                <div className="border border-border rounded-lg p-3 space-y-2.5">
                  <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <PlatformIcon platform="tiktok" size="xs" variant="glyph" />
                    TikTok options
                  </p>
                  <label className="block text-[11px] text-muted-foreground">
                    Privacy
                    <select
                      value={ttPrivacy}
                      onChange={(e) => setTtPrivacy(e.target.value)}
                      className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
                    >
                      <option value="">Auto (public if allowed)</option>
                      <option value="PUBLIC_TO_EVERYONE">Public</option>
                      <option value="MUTUAL_FOLLOW_FRIENDS">Friends</option>
                      <option value="FOLLOWER_OF_CREATOR">Followers</option>
                      <option value="SELF_ONLY">Only me</option>
                    </select>
                  </label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-1.5 text-[11px] text-foreground">
                      <input type="checkbox" checked={ttDisableComment} onChange={(e) => setTtDisableComment(e.target.checked)} />
                      Disable comments
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-foreground">
                      <input type="checkbox" checked={ttDisableDuet} onChange={(e) => setTtDisableDuet(e.target.checked)} />
                      Disable duet
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-foreground">
                      <input type="checkbox" checked={ttDisableStitch} onChange={(e) => setTtDisableStitch(e.target.checked)} />
                      Disable stitch
                    </label>
                  </div>
                </div>
              )}

              {/* Google Business Profile options */}
              {gmbAccount && (
                <div className="border border-border rounded-lg p-3 space-y-2.5">
                  <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <PlatformIcon platform="gmb" size="xs" variant="glyph" />
                    Google Business options
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-[11px] text-muted-foreground">
                      Post type
                      <select
                        value={gmbTopicType}
                        onChange={(e) => setGmbTopicType(e.target.value as 'STANDARD' | 'EVENT' | 'OFFER')}
                        className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
                      >
                        <option value="STANDARD">Update</option>
                        <option value="EVENT">Event</option>
                        <option value="OFFER">Offer</option>
                      </select>
                    </label>
                    <label className="block text-[11px] text-muted-foreground">
                      Button
                      <select
                        value={gmbCtaType}
                        onChange={(e) => setGmbCtaType(e.target.value)}
                        className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
                      >
                        <option value="">None</option>
                        <option value="LEARN_MORE">Learn more</option>
                        <option value="BOOK">Book</option>
                        <option value="ORDER">Order</option>
                        <option value="SHOP">Shop</option>
                        <option value="SIGN_UP">Sign up</option>
                        <option value="CALL">Call</option>
                      </select>
                    </label>
                  </div>
                  {gmbCtaType && gmbCtaType !== 'CALL' && (
                    <label className="block text-[11px] text-muted-foreground">
                      Button link
                      <input
                        value={gmbCtaUrl}
                        onChange={(e) => setGmbCtaUrl(e.target.value)}
                        placeholder="https://…"
                        className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
                      />
                    </label>
                  )}
                  {(gmbTopicType === 'EVENT' || gmbTopicType === 'OFFER') && (
                    <div className="space-y-2">
                      <label className="block text-[11px] text-muted-foreground">
                        {gmbTopicType === 'OFFER' ? 'Offer title' : 'Event title'}
                        <input
                          value={gmbEventTitle}
                          onChange={(e) => setGmbEventTitle(e.target.value)}
                          className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
                        />
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block text-[11px] text-muted-foreground">
                          Starts
                          <input
                            type="datetime-local"
                            value={gmbEventStart}
                            onChange={(e) => setGmbEventStart(e.target.value)}
                            className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
                          />
                        </label>
                        <label className="block text-[11px] text-muted-foreground">
                          Ends
                          <input
                            type="datetime-local"
                            value={gmbEventEnd}
                            onChange={(e) => setGmbEventEnd(e.target.value)}
                            className="mt-1 w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-accent"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Schedule + Queue controls */}
            <div className="px-3 pb-3">
              <div className="grid grid-cols-2 gap-2">
                {/* The native input stays invisible over this label and only
                    provides the picker. Today's date is display only: nothing
                    is scheduled until the user opens the picker. */}
                <div
                  className={cn(
                    'relative flex items-center gap-2 bg-input border border-border rounded-md px-3 py-2 focus-within:ring-1 focus-within:ring-accent',
                    queueId && 'opacity-50'
                  )}
                >
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className={cn('flex-1 truncate text-xs', scheduleAt ? 'text-foreground' : 'text-muted-foreground')}>
                    {scheduleAt
                      ? new Date(scheduleAt).toLocaleString('en-US', { ...US_DATE, hour: 'numeric', minute: '2-digit' })
                      : new Date().toLocaleDateString('en-US', US_DATE)}
                  </span>
                  <input
                    type="datetime-local"
                    aria-label="Schedule date and time"
                    value={scheduleAt}
                    onChange={(e) => {
                      setScheduleAt(e.target.value);
                      if (e.target.value) setQueueId('');
                    }}
                    onClick={(e) => openSchedulePicker(e.currentTarget)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openSchedulePicker(e.currentTarget);
                      }
                    }}
                    disabled={!!queueId}
                    className="absolute inset-0 w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                  />
                  {scheduleAt && (
                    <button
                      type="button"
                      onClick={() => setScheduleAt('')}
                      aria-label="Clear scheduled date"
                      className="relative shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 bg-input border border-border rounded-md px-3 py-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <select
                    value={queueId}
                    onChange={(e) => {
                      setQueueId(e.target.value);
                      if (e.target.value) setScheduleAt('');
                    }}
                    disabled={!!scheduleAt}
                    className="flex-1 bg-transparent text-xs text-foreground outline-none disabled:opacity-50"
                  >
                    <option value="">No queue</option>
                    {queues.map((q) => (
                      <option key={q.id} value={q.id}>
                        Add to {q.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40 transition-colors"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {editPostId ? (
              <button
                onClick={() => updatePost.mutate()}
                disabled={!hasContent || connectedSelected.length === 0 || updatePost.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent text-accent-foreground rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors"
              >
                {updatePost.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {queueId
                  ? 'Save & Add to Queue'
                  : scheduleAt
                    ? 'Save & Schedule'
                    : isUnscheduling
                      ? 'Unschedule & Save as Draft'
                      : 'Save Changes'}
              </button>
            ) : (
              <>
                <button
                  onClick={() => createPost.mutate()}
                  disabled={!hasContent || connectedSelected.length === 0 || createPost.isPending}
                  className="px-3 py-1.5 text-xs bg-muted/60 text-foreground border border-border rounded-md hover:bg-muted disabled:opacity-50 transition-colors"
                >
                  {queueId ? 'Add to Queue' : scheduleAt ? 'Schedule' : 'Save Draft'}
                </button>
                <button
                  onClick={() => publishNow.mutate()}
                  disabled={!hasContent || connectedSelected.length === 0 || publishNow.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent text-accent-foreground rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors"
                >
                  <Send className="h-3.5 w-3.5" />
                  Publish Now
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {pickerOpen && (
        <MediaLibraryPicker
          onClose={() => setPickerOpen(false)}
          onSelect={addFromLibrary}
        />
      )}
    </div>
  );
}
